/**
 * Care-team scoping — minimum-necessary access to one patient's chart (backlog T6).
 *
 * **Being a member of the right practice must not by itself open any chart.** Org membership plus a
 * role is not enough on its own — a clinician browsing a colleague's patient, or a neighbour's, or
 * a celebrity's, is a HIPAA minimum-necessary violation (45 CFR §164.502(b)) whether or not they had
 * any business being in the practice at all, and it is the single most common real-world PHI
 * incident in an EMR.
 *
 * This composes two modules rather than re-deriving either:
 *
 *   - `authorization.ts` answers "may this role read charts *at all*". Nothing here re-implements a
 *     role check; every capability question goes through `decide`.
 *   - `confidentiality.ts` answers "is a record carrying this label visible to a viewer permitted
 *     these reasons". Nothing here inspects `label.reasons` directly — `isVisibleTo` is the only
 *     path, so the label logic cannot drift between this module and the export and audit paths that
 *     also consult it (`docs/roadmap.md` §9, invariant 4).
 *
 * The three questions are genuinely independent, and all three must pass: role, relationship, and
 * label. An attending physician is still refused a record whose restriction reason they are not
 * permitted; a permitted viewer with no relationship to the patient is still refused.
 *
 * **Why a result object rather than a boolean.** A boolean cannot carry the two facts the caller is
 * obliged to record, and both are obligations rather than conveniences:
 *
 *   1. **The access basis.** `docs/roadmap.md` §3.2 requires every audit event to say "under what
 *      access basis" — normal care-team access versus break-glass. A boolean forces the route to
 *      re-derive that from its own inputs, which is a second implementation of this decision and
 *      exactly the drift `claude-kit/rules/invariants.md` ("Centralize the actual authorization
 *      decision") exists to prevent.
 *   2. **The scope.** Front-desk and billing staff legitimately reach a patient record without any
 *      treatment relationship, but only its administrative surface. Returning `true` to them would
 *      hand the caller no way to know it must project demographics rather than the chart.
 *
 * The denial reason is carried for the same reason: a refusal that a route cannot classify becomes
 * one generic 403, and "you may not read this chart" and "your role may not read charts" are
 * different incidents to a privacy officer reading the audit log.
 */

import {
  type ConfidentialityLabel,
  type RestrictionReason,
  isVisibleTo,
} from './confidentiality.ts';
import { type Grants, type Role, decide, isRole } from './authorization.ts';

/**
 * The care-team relationship between a user and a patient. Explicitly modelled rather than a
 * boolean, because the relationships behave differently and a boolean would force every difference
 * into the call site:
 *
 *   - `attending` — has primary responsibility for this patient's care.
 *   - `covering` — cross-coverage, call, or float: real treatment responsibility, time-boxed. Bounds
 *     are enforced when the relationship row is written, not here; this module takes the answer.
 *   - `consulting` — asked to advise on a specific question.
 *   - `referring` — the outside provider who sent the patient in, or who is being referred to. The
 *     only relationship an external `referring_provider` may hold.
 *   - `none` — no relationship. Not a missing value: the fail-closed default, and the value an
 *     unrecognised relationship string collapses to.
 */
export const CARE_TEAM_RELATIONSHIPS = [
  'attending',
  'covering',
  'consulting',
  'referring',
  'none',
] as const;

export type CareTeamRelationship = (typeof CARE_TEAM_RELATIONSHIPS)[number];

export function isCareTeamRelationship(value: string): value is CareTeamRelationship {
  return (CARE_TEAM_RELATIONSHIPS as readonly string[]).includes(value);
}

/**
 * Every relationship a route may actually *write* to a `care_team_memberships` row (backlog X20).
 * `'none'` is excluded on purpose — it is the fail-closed default an absent or unrecognised row
 * collapses to, never a value worth persisting; a route that let a caller write it would produce a
 * row that reads as "an active membership exists" while granting nothing, which is confusing at best
 * and, per this file's own comment on `TREATMENT_RELATIONSHIPS_BY_ROLE`, exactly the value that must
 * never appear in a stored row.
 */
export const ASSIGNABLE_CARE_TEAM_RELATIONSHIPS = CARE_TEAM_RELATIONSHIPS.filter(
  (relationship): relationship is Exclude<CareTeamRelationship, 'none'> => relationship !== 'none',
);
export type AssignableCareTeamRelationship = (typeof ASSIGNABLE_CARE_TEAM_RELATIONSHIPS)[number];

/**
 * Which relationships constitute a treatment relationship *for each role*. Role interacts with
 * relationship rather than being independent of it, which is the distinction backlog T6 turns on: a
 * practice administrator's authority over a practice is not a treatment relationship, and no
 * care-team row makes it one.
 *
 * `none` must never appear in any row — that would silently defeat the fail-closed property this
 * whole module exists for, and it is asserted by test rather than trusted.
 *
 * Rows are empty for every administrative role. That is not "these roles are denied": they reach a
 * patient record through the administrative branch of `decideChartAccess`, which does not consult
 * this table at all. An empty row means "this role never has clinical access by relationship".
 *
 * `referring_provider` is limited to `referring` and `consulting`. An external provider cannot be
 * the attending on a chart in *our* practice, and if such a row existed it would be data corruption
 * rather than a relationship to honour.
 */
const TREATMENT_RELATIONSHIPS_BY_ROLE: Record<Role, readonly CareTeamRelationship[]> = {
  practice_admin: [],
  physician: ['attending', 'covering', 'consulting'],
  advanced_practice_provider: ['attending', 'covering', 'consulting'],
  nurse: ['attending', 'covering', 'consulting'],
  medical_assistant: ['attending', 'covering', 'consulting'],
  front_desk: [],
  billing: [],
  referring_provider: ['referring', 'consulting'],
  auditor: [],
  // Administrative basis only, across managed practices via RLS's reachable-org-set — never a
  // treatment relationship (BACKLOG.md T16), matching practice_admin's own empty row.
  reseller_admin: [],
};

/**
 * May a member holding `role` actually be assigned `relationship` (backlog X20's "assign care team
 * member" action)? The same table `decideChartAccess` consults for the *read* question, exposed here
 * for the *write* question — "assigning this relationship to a user in this role produces a row that
 * will actually grant something" — so that a care-team-assignment route validates against the one
 * table this module already owns rather than a second, driftable copy of the same role/relationship
 * mapping in the server layer (`claude-kit/rules/invariants.md`, "Centralize the actual authorization
 * decision, not just the default").
 *
 * Returns `false` for an unrecognised role rather than throwing, matching every other fail-closed
 * function in this module — a route calling this with a role string it cannot yet validate should
 * refuse the assignment, not crash.
 */
export function isTreatmentRelationshipForRole(
  role: string,
  relationship: AssignableCareTeamRelationship,
): boolean {
  if (!isRole(role)) return false;
  return TREATMENT_RELATIONSHIPS_BY_ROLE[role].includes(relationship);
}

/**
 * How much of the record this decision opens.
 *
 *   - `clinical` — the chart: notes, problems, medications, results, plus the administrative
 *     surface underneath it.
 *   - `administrative` — demographics, coverage, contact details, appointments. **No clinical
 *     content.** The caller must project accordingly; this is the value that makes minimum-necessary
 *     enforceable for front-desk and billing work.
 *   - `none` — nothing. Always paired with `allowed: false`.
 */
export const ACCESS_SCOPES = ['clinical', 'administrative', 'none'] as const;
export type AccessScope = (typeof ACCESS_SCOPES)[number];

/**
 * Why access was granted — the "access basis" every audit event must record
 * (`docs/roadmap.md` §3.2).
 *
 *   - `treatment` — a care-team relationship supports it. The ordinary, unremarkable case.
 *   - `administrative` — the role's job requires reaching the patient record without treating them.
 *   - `breakGlass` — neither of the above applied and the user asserted an override. This is the
 *     value that must produce the loud audit event and the alert (backlog T7).
 */
export const ACCESS_BASES = ['treatment', 'administrative', 'breakGlass'] as const;
export type AccessBasis = (typeof ACCESS_BASES)[number];

/**
 * Why access was refused. Closed, because a route that cannot distinguish these cannot report them
 * differently, and they are genuinely different events: `noTreatmentRelationship` is the normal,
 * expected refusal that a break-glass prompt should follow, while `roleCannotReadPatients` means
 * someone reached a chart route their job has no business on at all.
 */
export const CHART_ACCESS_DENIAL_REASONS = [
  /** No membership, or a role name this build does not recognise. Corrupt data or a bad migration. */
  'unrecognisedRole',
  /** The role holds neither `patient:read` nor `chart:read`. */
  'roleCannotReadPatients',
  /** The role may read charts, but has no treatment relationship with this patient. */
  'noTreatmentRelationship',
  /** The record's confidentiality label is not visible to this viewer's permitted reasons. */
  'confidentialityLabel',
  /** Break-glass was asserted with no usable justification, so it was refused outright. */
  'breakGlassMissingJustification',
  /** Break-glass was asserted by a role that does not hold `chart:break_glass`. */
  'roleCannotBreakGlass',
] as const;

export type ChartAccessDenialReason = (typeof CHART_ACCESS_DENIAL_REASONS)[number];

/**
 * A break-glass assertion. **Carries its justification as data, never as a bare boolean flag.**
 *
 * A boolean parameter would let break-glass quietly widen access at any call site that passed
 * `true`, with nothing for the audit event to record and nothing for a reviewer to read afterwards.
 * Requiring a justification string makes the recorded reason structurally inseparable from the
 * access it buys, and makes an accidental `breakGlass: true` impossible to write.
 *
 * The emptiness check here is a floor, not a validation policy: the route that accepts this from a
 * user should demand something substantive (a minimum length, a reason code, the patient encounter
 * it relates to) before it reaches this function. An empty or whitespace-only justification is a
 * refusal rather than a silently-allowed override, because "break glass with no reason given" is
 * indistinguishable from unaudited access.
 */
export interface BreakGlassAssertion {
  readonly justification: string;
}

export interface ChartAccessRequest {
  /** What the acting identity holds. Resolved server-side — never from the request body. */
  readonly grants: Grants;
  /**
   * This user's care-team relationship to *this* patient, as stored. A `string` rather than the
   * union because it arrives from the database; anything unrecognised collapses to `none`, which
   * denies. See `isCareTeamRelationship`.
   */
  readonly relationship: string;
  /** The confidentiality label on the record being reached (`confidentiality.ts`). */
  readonly label: ConfidentialityLabel;
  /** The restriction reasons this viewer is permitted. Empty means unrestricted records only. */
  readonly permittedReasons: readonly RestrictionReason[];
  /**
   * A break-glass assertion, or `null` for an ordinary access attempt. `null` rather than an
   * optional property so that every call site states its intent explicitly — under
   * `exactOptionalPropertyTypes` an omitted optional and a deliberate "no override" would otherwise
   * read identically at the call site, and this is not a decision to make by omission.
   */
  readonly breakGlass: BreakGlassAssertion | null;
}

export interface ChartAccessDecision {
  readonly allowed: boolean;
  /** How much of the record is open. `none` whenever `allowed` is false. */
  readonly scope: AccessScope;
  /** Why it was granted — for the audit event. `null` whenever `allowed` is false. */
  readonly basis: AccessBasis | null;
  /**
   * Was this granted *only* because break-glass was asserted? The caller must write the loud audit
   * event and fire the alert when this is true (backlog T7).
   *
   * False whenever ordinary access already sufficed, even if the caller also passed a break-glass
   * assertion. Reporting an override that was not needed would put a false alarm in front of a
   * privacy officer on every such request, and alarm fatigue is how a real break-glass event gets
   * scrolled past.
   */
  readonly usedBreakGlass: boolean;
  /** The justification, trimmed, exactly when `usedBreakGlass` is true. Otherwise `null`. */
  readonly justification: string | null;
  /** Why it was refused. `null` whenever `allowed` is true. */
  readonly denialReason: ChartAccessDenialReason | null;
}

function deny(denialReason: ChartAccessDenialReason): ChartAccessDecision {
  return {
    allowed: false,
    scope: 'none',
    basis: null,
    usedBreakGlass: false,
    justification: null,
    denialReason,
  };
}

function allow(scope: Exclude<AccessScope, 'none'>, basis: AccessBasis): ChartAccessDecision {
  return {
    allowed: true,
    scope,
    basis,
    usedBreakGlass: false,
    justification: null,
    denialReason: null,
  };
}

function allowByBreakGlass(justification: string): ChartAccessDecision {
  return {
    allowed: true,
    scope: 'clinical',
    basis: 'breakGlass',
    usedBreakGlass: true,
    justification,
    denialReason: null,
  };
}

/**
 * May this user reach this patient's record, and how much of it?
 *
 * Evaluation order is deliberate and each step is a distinct refusal:
 *
 *   1. **Role must be recognised.** No membership, or an unknown role name, denies.
 *   2. **Role must be able to reach a patient at all** (`patient:read` or `chart:read`).
 *   3. **Ordinary access, clinical branch**: the role holds `chart:read`, the stored relationship is
 *      a treatment relationship *for that role*, and the label is visible. All three, or no chart.
 *   4. **Ordinary access, administrative branch**: the role holds `patient:read` but not
 *      `chart:read` — its job is administrative — so no relationship is required and the scope is
 *      administrative. The label is still consulted, because `sensitivePatient` restricts access to
 *      the *patient* rather than to a subject matter, and a VIP's demographics are exactly what that
 *      reason exists to protect.
 *   5. **Break-glass, only if nothing above allowed it.** Evaluated last so that an unnecessary
 *      assertion never gets recorded as an override — see `usedBreakGlass`.
 *
 * Break-glass overrides the relationship requirement (step 3) and the confidentiality label (steps
 * 3 and 4). It does **not** override the role gate: a role that may not read charts cannot acquire
 * that ability by asserting an emergency, and only roles holding `chart:break_glass` may assert one
 * at all. `confidentiality.ts`'s `isVisibleTo` deliberately has no "permitted everything" shortcut
 * for this reason — the override is an explicit, audited decision made here, at the call site, and
 * never a flag threaded through the label logic.
 *
 * Fails closed: no relationship and no break-glass means no chart access, for every clinical role,
 * regardless of practice membership.
 */
export function decideChartAccess(request: ChartAccessRequest): ChartAccessDecision {
  const { grants, label, permittedReasons, breakGlass } = request;

  if (!isRole(grants.role)) {
    return deny('unrecognisedRole');
  }

  const mayReadChart = decide(grants, 'chart:read');
  const mayReadPatient = decide(grants, 'patient:read');
  const labelVisible = isVisibleTo(label, permittedReasons);
  const relationship: CareTeamRelationship = isCareTeamRelationship(request.relationship)
    ? request.relationship
    : 'none';
  const hasTreatmentRelationship =
    TREATMENT_RELATIONSHIPS_BY_ROLE[grants.role].includes(relationship);

  const ordinary = ((): ChartAccessDecision => {
    if (!mayReadChart && !mayReadPatient) {
      return deny('roleCannotReadPatients');
    }
    if (mayReadChart) {
      if (!hasTreatmentRelationship) {
        return deny('noTreatmentRelationship');
      }
      if (!labelVisible) {
        return deny('confidentialityLabel');
      }
      return allow('clinical', 'treatment');
    }
    if (!labelVisible) {
      return deny('confidentialityLabel');
    }
    return allow('administrative', 'administrative');
  })();

  if (ordinary.allowed) {
    return ordinary;
  }
  if (breakGlass === null) {
    return ordinary;
  }

  const justification = breakGlass.justification.trim();
  if (justification === '') {
    return deny('breakGlassMissingJustification');
  }
  if (!decide(grants, 'chart:break_glass')) {
    return deny('roleCannotBreakGlass');
  }
  return allowByBreakGlass(justification);
}
